/* eslint-disable complexity */
import { inject } from 'mobx-react';
import * as React from 'react';

import { ClientLogger } from 'bernie-client';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import { PackageType } from 'models/multi-item/multi-item-types';

import { classNames } from 'uitk-react-utils';

import { FormFieldBinding } from './form-field-binding';
import { NewDateField } from './new-date-field';
import { SearchFormFieldProps } from 'components/common/search-form';

export interface DateRangeState {
  endDateFocus?: () => void;
  startDateFocus?: () => void;
}

export type LocalizedDateRateProps = LocalizedComponentProps & SearchFormFieldProps;

export interface DateRangeProps extends LocalizedDateRateProps {
  currentHotel?: CurrentHotelStore;
  fetchDatePickerRates?: () => Promise<object>;
  multiItem?: MultiItemStore;
}

const logger = ClientLogger.getLoggerWithIdentifier('pwa.component.searchform');

@withLocalization
@FormFieldBinding
@inject('currentHotel', 'multiItem')
export class DateRange extends React.Component<DateRangeProps, DateRangeState> {
  constructor(props) {
    super(props);

    this.state = {
      endDateFocus: undefined,
      startDateFocus: undefined,
    };

    this.handleChange = this.handleChange.bind(this);
    this.registerFocusMethod = this.registerFocusMethod.bind(this);
    this.validate = this.validate.bind(this);

    const { registerValidator } = props;

    registerValidator(() => this.validate('startDate', true));
    registerValidator(() => this.validate('endDate', true));
  }

  private handleChange(event) {
    const { updateModel } = this.props;

    if (typeof updateModel === 'function') {
      updateModel(event);
    }
  }

  private logDateRangeError(startDateError, endDateError, startDate, endDate) {
    logger.logEvent(
      new SystemEvent(SystemEventLevel.WARN, 'SEARCH_FORM_DATE_RANGE_ERROR'),
      `${startDateError} | ${endDateError}`,
      `startDate=${startDate} endDate=${endDate}`
    );
  }

  private updateModels(startDate: string, endDate: string) {
    this.handleChange({
      target: {
        name: 'startDate',
        value: startDate,
      },
    });

    this.handleChange({
      target: {
        name: 'endDate',
        value: endDate,
      },
    });
  }

  public render() {
    const {
      l10n,
      model,
      wrapperClassNames,
      currentHotel,
      autoSubmit,
      isSubmitted,
      fetchDatePickerRates,
      callToAction,
      multiItem,
    } = this.props;
    const { formatText } = l10n;
    const startDate = model && model.startDate;
    const endDate = model && model.endDate;
    const buttonText = autoSubmit
      ? callToAction || formatText('hotels.search.form.submit.label')
      : formatText('hotels.roomPicker.doneButton.label');

    const isFlightInSearchForm = multiItem?.multiItemContext?.packageType !== PackageType.HC;
    const wrapperClasses =
      wrapperClassNames ??
      classNames({
        'uitk-cell s-cell-1-1 m-cell-1-2 l-cell-1-4 xl-cell-1-4': isFlightInSearchForm,
        'uitk-cell s-cell-1-2 m-cell-1-4 l-cell-1-6 xl-cell-1-6': !isFlightInSearchForm,
      });
    const startDateError = this.validate('startDate', isSubmitted);
    const endDateError = this.validate('endDate', isSubmitted);

    const commonDateFieldProps = {
      startDate,
      endDate,
      placeholder: formatText('hotels.search.form.toDate.placeholder'),
      numDaysBookableInAdvance: SearchCriteriaModel.NUM_OF_DAYS_BOOKABLE_IN_ADVANCE,
      midnightBookingOffsetHours: SearchCriteriaModel.MIDNIGHT_BOOKING_OFFSET_IN_HOURS,
      buttonText,
      registerFocusMethod: this.registerFocusMethod,
      isDateless: currentHotel.searchCriteria.isDateless,
      updateModels: this.updateModels.bind(this),
    };

    if (logger && typeof logger.logEvent === 'function' && (startDateError || endDateError)) {
      this.logDateRangeError(startDateError, endDateError, startDate, endDate);
    }

    const routerLinkWrapper = children => <>{children}</>;

    const isFlightInSearch = multiItem && multiItem.getPackageType() && multiItem.getPackageType() !== PackageType.HC;
    const { startDateLabel, endDateLabel } = isFlightInSearch
      ? {
          startDateLabel: formatText('hotels.searchWizard.multiitem.departing.date'),
          endDateLabel: formatText('hotels.searchWizard.multiitem.returning.date'),
        }
      : {
          startDateLabel: formatText('hotels.search.form.fromDateHyphen.label'),
          endDateLabel: formatText('hotels.search.form.toDateHyphen.label'),
        };

    const dateFieldProps = {
      dateFieldWrapperClasses: wrapperClasses,
      endId: 'hotels-check-out',
      endInvalid: endDateError && endDateError.text,
      endLabel: endDateLabel,
      endName: 'endDate',
      endPlaceholder: formatText('hotels.search.form.toDate.placeholder'),
      routerLinkWrapper,
      startId: 'hotels-check-in',
      startInvalid: startDateError && startDateError.text,
      startLabel: startDateLabel,
      startName: 'startDate',
      startPlaceholder: formatText('hotels.search.form.toDate.placeholder'),
    };

    const rfrr = {
      end: ':ChangeDatesCheckOut',
      start: ':ChangeDatesCheckIn',
    };

    return (
      <NewDateField rfrr={rfrr} dateFieldProps={dateFieldProps} fetchDatePickerRates={fetchDatePickerRates} {...commonDateFieldProps} />
    );
  }

  private registerFocusMethod(fieldName: string, focusMethod: () => void) {
    this.setState({ [`${fieldName}Focus`]: focusMethod });
  }

  private validate(key, isSubmitted) {
    const { l10n, model, showErrorValidationOnSubmit } = this.props;
    const { startDateFocus, endDateFocus } = this.state;

    const { formatText } = l10n;

    const { endDate, startDate } = model;

    if (key === 'startDate') {
      const start = startDate && new Date(startDate);
      const message =
        isSubmitted && showErrorValidationOnSubmit
          ? 'hotels.search.validation.invalid.dateless.text'
          : 'hotels.search.validation.invalidFromDate.text';

      if (!this.isValidDate(start) && (!model.isDateless || (isSubmitted && showErrorValidationOnSubmit))) {
        return {
          text: formatText(message),
          focus: startDateFocus,
        };
      }
    }

    if (key === 'endDate') {
      const start = startDate && new Date(startDate);
      const end = endDate && new Date(endDate);
      const message =
        isSubmitted && showErrorValidationOnSubmit
          ? 'hotels.search.validation.invalid.dateless.text'
          : 'hotels.search.validation.invalidToDate.text';

      if (!this.isValidDate(end) && (!model.isDateless || (isSubmitted && showErrorValidationOnSubmit))) {
        return {
          text: formatText(message),
          focus: endDateFocus,
        };
      }

      if (this.isValidDate(start)) {
        const maxEndDate = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 28);

        if (end < start) {
          return {
            text: formatText('hotels.search.validation.invalidDateRange.text'),
            focus: endDateFocus,
          };
        }

        maxEndDate.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        if (end > maxEndDate) {
          return {
            text: formatText('hotels.search.validation.invalidDateDateRangeLength.text'),
            focus: endDateFocus,
          };
        }
      }
    }
  }

  private isValidDate(date: Date) {
    return date && date.toString() !== 'Invalid Date';
  }
}
